import React from 'react';
import { AppLink } from '../components/ui/AppLink';
import { I18n, LABEL_CATEGORY } from '../hooks/I18n';
import {
  defined,
  includesAll,
  Optional,
  range,
  returnIf,
  setDefault,
} from '../utils/Util';
import { RaceIdRangesConverter } from './RaceIdRangesConverter';

const PAGE_PATH = {
  year: '/year/race_year/',
  race: '/race/race_id/',
  race_list: '/race_list',
  circuit: '/circuit/circuit/',
} as const;

const LINK_DELIMITER = '~';
const LINK_INTERVAL = '-';

const VIEW_DELIMITER = ', ';
const VIEW_INTERVAL = '~';

export type RaceData = {
  // スプリント対応
  [key: string]: { year: number; round: number; index?: Optional<number> };
};

class RaceViewData {
  constructor(
    readonly year: number,
    readonly round: number,
    readonly index?: Optional<number>
  ) {}
}

export class RacesViewData {
  private static readonly instances = new WeakMap<RaceData, RacesViewData>();

  static getInstance(raceData: RaceData): RacesViewData {
    // ラウンドによる分割の結果をキャッシュするため
    // インスタンスを何個も作らないようにしている
    let instance = this.instances.get(raceData);
    if (!defined(instance)) {
      instance = new RacesViewData(raceData);
      this.instances.set(raceData, instance);
    }
    return instance;
  }

  private readonly racesData: Record<number, RaceViewData>;

  // 年からrace idの集合へのmap
  private readonly yearToRacesMap: Record<number, number[]> = {};
  private readonly raceIdRangesConverter;

  private constructor(raceData: RaceData) {
    this.racesData = {};
    Object.entries(raceData).map(([raceIdStr, { year, round, index }]) => {
      const raceId = Number(raceIdStr);
      this.racesData[raceId] = new RaceViewData(year, round, index);
      setDefault(this.yearToRacesMap, year, []).push(raceId);
    }, []);
    this.raceIdRangesConverter = new RaceIdRangesConverter(this);
  }

  get(raceId: Optional<string | number>): Optional<RaceViewData> {
    return returnIf(raceId, (raceId) => this.racesData[Number(raceId)]);
  }

  getFirstRaceId(year: number): number {
    return Object.entries(this.racesData)
      .filter(([, raceData]) => raceData.year === year)
      .reduce<number>(
        (firstRaceId, [raceId]) => Math.min(firstRaceId, Number(raceId)),
        Number.MAX_SAFE_INTEGER
      );
  }

  getLastRaceId(year: number): number {
    return Object.entries(this.racesData)
      .filter(([, raceData]) => raceData.year === year)
      .reduce<number>(
        (lastRaceId, [raceId]) => Math.max(lastRaceId, Number(raceId)),
        Number.MIN_SAFE_INTEGER
      );
  }

  getMinRound(year: number): number {
    return Object.entries(this.racesData)
      .filter(([, raceData]) => raceData.year === year)
      .reduce<number>(
        (minRound, [, raceViewData]) => Math.min(minRound, raceViewData.round),
        Number.MAX_SAFE_INTEGER
      );
  }

  getMaxRound(year: number): number {
    return Object.entries(this.racesData)
      .filter(([, raceData]) => raceData.year === year)
      .reduce<number>(
        (maxRound, [, raceViewData]) => Math.max(maxRound, raceViewData.round),
        Number.MIN_SAFE_INTEGER
      );
  }

  entryToAllRounds(ranges: RaceIdRanges): boolean {
    // スプリント対応
    // 年からround の配列へのマップ
    const roundsInYear: Record<number, number[]> = {};

    for (const raceId of ranges.toFilledArray()) {
      const raceViewData = this.get(raceId);
      if (defined(raceViewData)) {
        setDefault(roundsInYear, raceViewData.year, []).push(
          raceViewData.round
        );
      }
    }

    // round の配列がその年のround をすべて含んでいるかチェック
    // スプリント戦を考慮してrace_idではく round で比較する
    const entryToAllRounds = Object.entries(roundsInYear).every(
      ([year, rounds]) => {
        const allRounds: number[] = [];
        for (const raceId of this.yearToRacesMap[Number(year)] ?? []) {
          const raceViewData = this.get(raceId);
          if (defined(raceViewData)) {
            allRounds.push(raceViewData.round);
          }
        }
        return includesAll([...new Set(rounds)], [...new Set(allRounds)]);
      }
    );
    return entryToAllRounds;
  }

  convertForReadability(ranges: RaceIdRanges): RaceIdRanges {
    return this.raceIdRangesConverter.convertForReadability(ranges);
  }

  isYear(num: number): boolean {
    return Object.keys(this.yearToRacesMap).map(Number).includes(num);
  }
}

export class IntRange {
  constructor(readonly start: number, readonly end: number) {}

  toFilledArray(): number[] {
    return range(this.start, this.end);
  }
}

export class IntRanges {
  readonly ranges: IntRange[];
  constructor(value: string | IntRange[]) {
    if (Array.isArray(value)) {
      this.ranges = value.map((v) => new IntRange(v.start, v.end));
    } else {
      const result: IntRange[] = [];
      value.split(LINK_DELIMITER).forEach((v) => {
        const [start, end] = v
          .split(LINK_INTERVAL)
          .map((v) => returnIf(v, (v) => parseInt(v)));
        if (defined(start)) {
          result.push(new IntRange(start, defined(end) ? end : start));
        }
      });
      this.ranges = result;
    }
  }

  toFilledArray(): number[] {
    return this.ranges.reduce(
      (result, range) => result.concat(range.toFilledArray()),
      [] as number[]
    );
  }

  toArray<T, U>(
    state: U,
    createElementFunc: (
      v: number,
      array: (T | string)[],
      state: U
    ) => T | string = (v) => v.toString(),
    areSameFunc: (v1: number, v2: number) => boolean = (v1, v2) => v1 === v2,
    delimiter = VIEW_DELIMITER,
    interval = VIEW_INTERVAL
  ): (T | string)[] {
    const array: (T | string)[] = [];
    for (const [index, range] of this.ranges.entries()) {
      if (index > 0) {
        array.push(delimiter);
      }
      // year内の出場ラウンド表記は、スプリントレースを区別しない
      // ランキングからのリンクは、スプリントレースを区別する
      // createElementFuncはstateを持つので比較のために呼ぶと出力が変わる
      if (areSameFunc(range.start, range.end)) {
        array.push(createElementFunc(range.start, array, state));
      } else if (range.start + 1 === range.end) {
        array.push(createElementFunc(range.start, array, state));
        array.push(delimiter);
        array.push(createElementFunc(range.end, array, state));
      } else {
        array.push(createElementFunc(range.start, array, state));
        array.push(interval);
        array.push(createElementFunc(range.end, array, state));
      }
    }
    return array;
  }
}

// テスト目的のためのみ export する。LinkParser経由でインスタンス化すべき
export class RaceIdRanges extends IntRanges {}

class YearRanges extends IntRanges {}

class LinkParser {
  parseRaceIdRanges(link: string): RaceIdRanges {
    return new RaceIdRanges(link);
  }

  parseYearRanges(link: string): YearRanges {
    return new YearRanges(link);
  }

  parseAsStringArray(link: string): string[] {
    // 不正な文字列を想定して '-' で分割し、空文字を除く
    return link
      .split(new RegExp(`[${LINK_INTERVAL}${LINK_DELIMITER}]`))
      .filter((v) => v.length > 0);
  }
}

const createCircuitLinkElement = (
  circuit: string,
  i18n: I18n
): React.ReactElement => {
  const circuits = new LinkParser().parseAsStringArray(circuit);
  const elements: (React.ReactElement | string)[] = [];
  for (const [index, value] of circuits.entries()) {
    if (index > 0) {
      elements.push(VIEW_DELIMITER);
      // カンマの手前で区切る
      elements.push(<wbr key={index} />);
    }
    const element = (
      <AppLink key={value} to={{ pathname: PAGE_PATH.circuit + `${value}` }}>
        {i18n.DATA.localize(value, 'circuit')}
      </AppLink>
    );
    elements.push(element);
  }
  return <>{elements}</>;
};

const createRaceIdLinkElement = (
  raceId: string,
  raceData: RaceData,
  i18n: I18n
): React.ReactElement => {
  const racesViewData = RacesViewData.getInstance(raceData);
  const ranges = new LinkParser().parseRaceIdRanges(raceId);
  const intRanges = racesViewData.convertForReadability(ranges);
  const raceIdElement = intRanges.toArray(
    {},
    (v, array: (React.ReactElement | string)[], state) =>
      createLinkFuncForRaceId(raceData, v, array, state)
  );
  // 達成レース一覧へのリンク
  const raceLink = <RaceListLink link={raceId} i18n={i18n}></RaceListLink>;
  return (
    <>
      {raceIdElement} &nbsp; {raceLink}
    </>
  );
};

const createLinkFuncForRaceId = (
  raceData: RaceData,
  id: number,
  array: (React.ReactElement | string)[],
  state: {
    currentYear?: Optional<number>;
  }
) => {
  const elements: (React.ReactElement | string)[] = [];
  const { year, round, index } = raceData[id] ?? {};
  if (state.currentYear !== year) {
    if (array.at(-1) === VIEW_DELIMITER) {
      // 年の手前のカンマでのみ区切る
      elements.push(<wbr key={id} />);
    }
    elements.push(`${String(year)} Rd.`);
    state.currentYear = year;
  }
  // スプリントレースは "-n" で区別する
  elements.push(
    `${String(round)}${index !== undefined ? `-${index + 1}` : ''}`
  );
  return (
    <AppLink key={id} to={{ pathname: PAGE_PATH.race + `${id}` }}>
      {elements}
    </AppLink>
  );
};

const createYearLinkElement = (raceYear: string): React.ReactElement => {
  const ranges = new LinkParser().parseYearRanges(raceYear);
  return (
    <>
      {ranges.toArray({}, (id: number) => (
        <AppLink key={id} to={{ pathname: PAGE_PATH.year + `${id}` }}>
          {`${id} `}
        </AppLink>
      ))}
    </>
  );
};

const RaceListLink: React.FC<{
  link: string;
  i18n: I18n;
}> = ({ link, i18n }) => {
  // 達成レース一覧へのリンク
  const linkProp = {
    pathname: PAGE_PATH.race_list,
    search: `visible_row_ids=${link}`,
    hash: 'result',
  };
  return (
    <AppLink to={linkProp}>
      {i18n.LABELS.localize('link_detail', LABEL_CATEGORY.TABLE_COMMON)}
    </AppLink>
  );
};

export interface LinkValues {
  raceYear?: Optional<string>;
  raceId?: Optional<string>;
  circuit?: Optional<string>;
}

export const createLinkElement = (
  additionalValues: LinkValues,
  raceData: RaceData,
  i18n: I18n
): React.ReactElement | undefined => {
  const elements = [];
  if (additionalValues.circuit !== undefined) {
    elements.push(createCircuitLinkElement(additionalValues.circuit, i18n));
  }
  if (additionalValues.raceId !== undefined) {
    elements.push(
      createRaceIdLinkElement(additionalValues.raceId, raceData, i18n)
    );
  }
  if (additionalValues.raceYear !== undefined) {
    elements.push(createYearLinkElement(additionalValues.raceYear));
  }
  return elements.length > 0 ? <>{elements}</> : undefined;
};

export const createRoundsInfo = (races: string, raceData: RaceData): string => {
  const racesViewData = RacesViewData.getInstance(raceData);
  const ranges = new LinkParser().parseRaceIdRanges(races);
  if (!(ranges instanceof RaceIdRanges)) {
    return ''; // 想定外のケース
  } else {
    if (racesViewData.entryToAllRounds(ranges)) {
      // 全てのラウンドにエントリーしている場合は何も表示しない
      return '';
    } else {
      const r = racesViewData.convertForReadability(ranges);
      const resultArray = r.toArray(
        {},
        (v) => racesViewData.get(v)?.round.toString(),
        // 同一年のデータしか存在しないことが前提なのでyearの比較はしない
        // スプリントレースを同一とみなすのでroundのみを比較する
        (v1, v2) =>
          racesViewData.get(v1)?.round === racesViewData.get(v2)?.round
      );
      return resultArray.join('');
    }
  }
};

export const expandRowIds = (rowIds: string): string[] => {
  // 数値が含まれていなければ配列として展開
  const firstNumber = /[0-9]+/.exec(rowIds)?.[0];
  if (defined(firstNumber)) {
    return new IntRanges(rowIds).toFilledArray().map((v) => String(v));
  } else {
    // 不正な文字列を想定して '-' で分割し、空文字を除く
    return rowIds
      .split(new RegExp(`[${LINK_INTERVAL}${LINK_DELIMITER}]`))
      .filter((v) => v.length > 0);
  }
};
